/**
 * Premium "Gradient Hero" intro — Allyver web-hero look.
 *
 * Visual design:
 *  - Big animated brand-gradient backdrop (turquoise → violet → pink) drifts
 *    slowly. A dotted pattern overlays for depth.
 *  - Headline reveals with a moving accent gradient text-clip (sweep).
 *  - Floating glass pill chip + bottom CTA hint.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors, brandUrl,
} from "../../reel/sceneShared.js";

export const gradientHero: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Lokal verkaufen. Global wachsen.";
  const sub = inputs.subtext ?? "Allyver verbindet Händler, Kunden und Vertriebspartner in einem System.";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.62;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const eyeSize = typeSize("sm", ctx.format) * 0.7;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const sweepId = `${clipPrefix}-sweep`;
  const headId = `${clipPrefix}-head`;
  const headInnerId = `${clipPrefix}-headinner`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;
  const chipId = `${clipPrefix}-chip`;
  const urlId = `${clipPrefix}-url`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div id="${sweepId}" style="position:absolute;inset:0;background:linear-gradient(105deg, transparent 35%, ${withAlpha(themeColors.fg, 0.08)} 48%, ${withAlpha(accent, 0.5)} 50%, ${withAlpha(themeColors.fg, 0.08)} 52%, transparent 65%);transform:translateX(-120%);mix-blend-mode:screen;pointer-events:none;z-index:3;"></div>

  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 32 : 24}px;padding:96px;z-index:5;">
    <div id="${chipId}" style="display:inline-flex;align-items:center;gap:14px;padding:14px 24px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.08)};border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize * 0.78}px;letter-spacing:5px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.92)};opacity:0;">
      <span style="width:10px;height:10px;border-radius:9999px;background:${accent};box-shadow:0 0 14px ${accent};"></span>
      <span>Allyver</span>
      <span style="opacity:0.6;">·</span>
      <span style="color:${accent};">v2026</span>
    </div>

    <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;">— Allyver Hero —</div>

    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;text-align:center;text-wrap:balance;max-width:${isVertical ? 920 : 1500}px;color:${themeColors.fg};opacity:0;">
      <span id="${headInnerId}" style="background:linear-gradient(105deg, ${themeColors.fg} 0%, ${themeColors.fg} 35%, ${accent} 50%, ${themeColors.fg} 65%, ${themeColors.fg} 100%);background-size:200% 100%;background-position:100% 50%;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:transparent;display:inline-block;">${escapeHtml(headline)}</span>
    </div>

    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.8)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.4;opacity:0;">${escapeHtml(sub)}</div>` : ""}

    <div id="${urlId}" style="display:inline-flex;align-items:center;gap:12px;font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${eyeSize * 0.72}px;letter-spacing:3px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.7)};opacity:0;">
      <span style="width:8px;height:8px;border-radius:9999px;background:${accent};box-shadow:0 0 10px ${accent};"></span>
      <span>${escapeHtml(brandUrl ?? "allyver.com")}</span>
    </div>
  </div>
</div>`;

  const init = [
    `gsap.set("#${chipId}", { opacity: 0, y: -16 });`,
    `gsap.set("#${eyeId}", { opacity: 0, y: -10 });`,
    `gsap.set("#${headId}", { opacity: 0, y: 28 });`,
    `gsap.set("#${headInnerId}", { backgroundPosition: "100% 50%" });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
    `gsap.set("#${urlId}", { opacity: 0, y: 14 });`,
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${chipId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"back.out(1.4)\"" }, 0.1),
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.3),
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.7, ease: "raw:\"power3.out\"" }, 0.45),
    // Gradient sweeps across the headline.
    t.to(`#${headInnerId}`, { backgroundPosition: "0% 50%", duration: 1.4, ease: "raw:\"power2.inOut\"" }, 0.7),
    // Light leak sweep across the whole frame.
    t.to(`#${sweepId}`, { x: "120%", duration: 1.05, ease: "raw:\"power2.inOut\"" }, 0.55),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.95));
  tweens.push(t.to(`#${urlId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.15));

  // Continuous slow gradient sheen during dwell.
  const dwell = Math.max(0.6, clipDurationSec - 2.0);
  tweens.push(t.to(`#${headInnerId}`, { backgroundPosition: "-100% 50%", duration: dwell, ease: "raw:\"sine.inOut\"" }, 2.1));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
